import map from "lodash/fp/map";
import assign from "lodash/fp/assign";
import each from "lodash/fp/each";
import isNil from "lodash/fp/isNil";
import filter from "lodash/fp/filter";
import moment from "moment";
import { getContractRidePrice } from "../ride/ride.transforms";
import constants from "../../core/constants";

const formatter = (value) =>
  isNil(value) ? value : value * constants.currencyBy;

class RidesPageTransform {
  constructor($injector, Events, Lists) {
    "ngInject";
    this.Lists = Lists;
    this.Contracts = $injector.get("Contracts");
    this.City = $injector.get("City");
    this.Events = Events;
    this.BoardingCheckin = $injector.get("BoardingCheckin");
    // NOTE: name is used for (user roles)
    this.onSelectCorporateEvent = Events.getSelectCorporateEvent();
    this.onSelectBusTypeEvent = Events.getSelectBusEvent();
    this.onSelectRouteEvent = Events.getSelectRouteEvent();
    this.onSelectRideCategoryEvent = Events.getSelectRideCategoryEvent();
    this.onSelectSupplyCategoryEvent = Events.getSelectSupplyCategoryEvent();
    this.onSelectContractorEvent = Events.getSelectContractorEvent();
    this.onSelectCompanyEvent = Events.getSelectCompanyEvent();
    this.errorEvent = this.Events.eventsType.onShowErrorNotification;
    Events.on(this.onSelectCorporateEvent, this.getActiveContract.bind(this));
    Events.on(this.onSelectBusTypeEvent, this.getBusType.bind(this));
    Events.on(this.onSelectRouteEvent, this.getRouteType.bind(this));
    Events.on(this.onSelectRideCategoryEvent, this.getRideCategory.bind(this));
    Events.on(
      this.onSelectSupplyCategoryEvent,
      this.onSelectSupplyCategory.bind(this)
    );
    Events.on(this.onSelectContractorEvent, this.onSelectSupply.bind(this));
    Events.on(this.onSelectCompanyEvent, this.onSelectSupply.bind(this));

    this.item = {
      title: "Ride",
      state: "ride",
      name: "ride",
    };

    this.findAddOptionsFieldName = (fields, field) => {
      return fields.find((f) => f.name === field);
    };

    this.fields = [
      { name: "route", title: "Route" },
      { name: "date", title: "Date", isSortable: true },
      { name: "captain", title: "Captain" },
      {
        name: "vendor",
        title: "Vendor",
        getValue: (item) => {
          if (!item) return "";
          return (item.company || item.contractor || { name_i18n: {} })
            .name_i18n.en;
        },
      },
      { name: "bus", title: "Bus", value: "plates" },
      { name: "bus_type", title: "Bus Type" },
      { name: "status", title: "Status" },
      { name: "capacity", title: "Capacity" },
      { name: "price_tag", title: "Price" },
    ];

    this.invoicesFields = [
      ...this.fields,
      { name: "finances", title: "Finances" },
    ];

    this.filters = [
      {
        name: "route",
        title: "Route",
        size: 25,
        icon: "linear_scale",
        resource: "Route",
      },
      {
        name: "captain",
        title: "Captains",
        icon: "person_outline",
        resource: "Captain",
        size: 25,
        hasExact: true,
        exactFields: ["name", "phone"],
      },
      {
        name: "status",
        title: "Status",
        icon: "people_outline",
        list: "rideStatus",
        multiple: true,
      },
      {
        name: "category",
        title: "Ride Category",
        icon: "directions_car",
        list: "rideCategoryList",
        multiple: true,
      },
      {
        name: "arrived_first_station",
        title: "Arrived First Station",
        icon: "people_outline",
        list: "arrivedFirstStation",
      },
      { name: "date", title: "Select date", icon: "date_range", isDate: true },
      {
        name: "captain_assigned",
        title: "Assigned Captin",
        icon: "person_outline",
        list: "unassignedCaptain",
      },
      {
        name: "is_vendor_assigned",
        title: "Assigned Vendor",
        icon: "local_shipping",
        list: "unassignedVendor",
      },
      {
        name: "has_bookings",
        title: "Has Bookings",
        icon: "local_activity",
        list: "hasBookings",
      },
      {
        name: "is_dynamic",
        title: "Dynamic",
        icon: "directions_car",
        list: "rideType",
      },
      {
        name: "private_bus_request",
        title: "Private Bus Request",
        icon: "airport_shuttle",
        list: "isPrivateBusReq",
      },
    ];
    // PricingVersionValue : Fixed represent business rides & Dynamic represent non-business rides
    this.addOptions = {
      title: "Add New Ride",
      icon: "add",
      city: undefined,
      fields: [
        {
          resource: "Route",
          name: "route",
          title: "Route",
          isResource: true,
          isRequired: true,
          onChange: this.onSelectRouteEvent,
        },
        {
          name: "ride_category",
          title: "Ride Category",
          value: "",
          list: "rideCategoryList",
          isRequired: true,
          onChange: this.onSelectRideCategoryEvent,
        },
        {
          resource: "Corporate",
          name: "corporate",
          title: "Corporate",
          isResource: true,
          isRequired: true,
          renderIf: '{{ride_category}}.pricingVersionValue === "fixed"',
          onChange: this.onSelectCorporateEvent,
        },
        {
          resource: "Bustype",
          name: "bus_type",
          title: "Bustype",
          isResource: true,
          isRequired: true,
          onChange: this.onSelectBusTypeEvent,
        },
        {
          name: "fully_book_ride",
          title: "Fully Book Ride?",
          isRadio: true,
          renderIf: '{{ride_category}}.pricingVersionValue === "fixed"',
          isRequired: true,
        },
        {
          name: "ride_price",
          title: "Ride Price",
          type: "number",
          isText: true,
          isRequired: true,
          renderIf: '{{ride_category}}.pricingVersionValue === "fixed"',
          formatter,
        },
        {
          name: "supply_category",
          title: "Supply Category",
          value: null,
          list: "supplyCategoryList",
          isRequired: true,
          onChange: this.onSelectSupplyCategoryEvent,
        },
        {
          resource: "Contractor",
          name: "contractor",
          title: "Contractor",
          isResource: true,
          isRequired: true,
          renderIf: '{{supply_category}}.value === "contractor"',
          onChange: this.onSelectContractorEvent,
        },
        {
          resource: "Company",
          name: "company",
          title: "Company",
          isResource: true,
          isRequired: true,
          renderIf: '{{supply_category}}.value === "company"',
          onChange: this.onSelectCompanyEvent,
        },
        {
          resource: "Captain",
          name: "captain",
          title: "Captain",
          isResource: true,
          isRequired: true,
          hasExact: true,
          exactFields: ["name", "phone"],
        },
        {
          resource: "Bus",
          name: "bus",
          title: "Bus",
          isListResource: true,
          isRequired: true,
          requires: "captain",
          extraParamsFields: ["supply_category", "contractor", "company"],
          listTransformer: "BusesTransforms",
        },
        {
          name: "date",
          title: "Date",
          dateTime: true,
          isRequired: true,
          minDate: moment(),
        },
        {
          name: "captain_price",
          title: "Captain Price",
          type: "number",
          isText: true,
          formatter,
          hint: "Leave it empty if there is no custom price",
        },
        {
          name: "pricing_parameters",
          isPricingParameters: true,
          renderIf:
            '{{ride_category}}.value && {{ride_category}}.pricingVersionValue !== "fixed" && {{route}}.value && {{route}}.pricingConfig',
        },
        {
          name: "check_in_method",
          renderIf:
            "{{ride_category}}.value && {{route}}.value && {{ride_category}}.pricingVersionValue !== 'fixed' || {{corporate}}.value",
          title: "Check in method",
          isCompoundSwitch: true,
          hasNote: true,
          note: "you must choose one method at leaset",
          requiredOnCount: 1,
          children: [
            {
              label: "List",
              name: "is_list_checkin_allowed",
              value: true,
            },
            {
              label: "Qr code",
              name: "is_qr_code_allowed",
              value: false,
            },
          ],
          isRequired: true,
        },
      ],
    };
  }

  justify(data) {
    const getRideFinance = (items) => {
      return items.reduce((acc, current) => {
        const listTitle = current.type.name;
        const amount = current.amount / constants.currencyBy;

        return acc.length > 1
          ? `${acc} ,${listTitle} ${amount}`
          : `${listTitle} ${amount}`;
      }, "");
    };
    const format = (item) => {
      item = item._source ? assign({ _id: item._id }, item._source) : item;
      item.price /= constants.currencyBy;
      item.price_tag = item.price + " " + item.currency;
      item.date = moment(item.date).format(constants.dateTimeFormat);
      item.capacity =
        item.bookings && item.bookings.count + "/" + item.bookings.maximum;
      item.finances =
        item.invoice_items && item.invoice_items.length
          ? getRideFinance(item.invoice_items)
          : "No finance info";

      return item;
    };

    return map(format)(data);
  }

  getBusType(selected) {
    this.busType = selected;
    const fieldsBelow = filter((field) => {
      return (
        field.name !== "bus_type" &&
        field.name !== "ride_category" &&
        field.name !== "corporate" &&
        field.name !== "route" &&
        field.name !== "captain" &&
        field.name !== "bus" &&
        field.name !== "supply_category" &&
        field.name !== "contractor" &&
        field.name !== "company"
      );
    })(this.addOptions.fields);
    if (!isNil(this.route) && !isNil(this.busType)) {
      each((field) => {
        field.isDisabled = false;
        field.value = null;
      })(fieldsBelow);
    }
    this.applyRidePrice();
  }

  getRouteType(selected) {
    this.route = selected;
    const fieldsBelow = filter((field) => {
      return (
        field.name !== "ride_category" &&
        field.name !== "corporate" &&
        field.name !== "route" &&
        field.name !== "captain" &&
        field.name !== "bus" &&
        field.name !== "supply_category" &&
        field.name !== "contractor" &&
        field.name !== "company"
      );
    })(this.addOptions.fields);
    if (!isNil(this.route)) {
      each((field) => {
        field.isDisabled = false;
        field.value = null;
      })(fieldsBelow);
    }
    this.applyRidePrice();
    this.getPricingParams(this.route.city);
  }

  getRideCategory() {
    if (!isNil(this.route)) this.getPricingParams(this.route.city);
  }

  getCheckInConfiguration(level, key) {
    this.BoardingCheckin.getConfig(level, key).$promise.then(
      (response) => {
        const fields = this.addOptions.fields;
        const checkInAddOptionsField = this.findAddOptionsFieldName(
          fields,
          "check_in_method"
        );
        if (response) {
          const [listCheckin, qrCode] = checkInAddOptionsField.children;
          listCheckin.value = response.is_list_allowed;
          qrCode.value = response.is_qr_code_allowed;
        }
      },
      (errResponse) => {
        this.Events.emit(this.errorEvent, errResponse.data);
      }
    );
  }

  getPricingParams(cityId) {
    this.City.get(cityId).$promise.then((response) => {
      this.addOptions.city = response;
      const fields = this.addOptions.fields;
      const pricingParamterAddOptionsField = this.findAddOptionsFieldName(
        fields,
        "pricing_parameters"
      );
      const routeAddOptionsField = this.findAddOptionsFieldName(
        fields,
        "route"
      );

      pricingParamterAddOptionsField.currency = response.currency;
      const rideCategoryAddOptionsField = this.findAddOptionsFieldName(
        fields,
        "ride_category"
      );
      // Displaying initial pricing params in the Dialog if the city-category has pricing params.
      if (response.ride_categories) {
        const selectedCategoryDetails = response.ride_categories.find(
          (categoryItem) =>
            categoryItem.category === rideCategoryAddOptionsField.value
        );
        pricingParamterAddOptionsField.active = true;
        const cityCategoryPricing = selectedCategoryDetails.features.find(
          (feature) => feature.name === "pricing_version"
        );
        if (
          !isNil(cityCategoryPricing) &&
          cityCategoryPricing.value === "dynamic"
        ) {
          rideCategoryAddOptionsField.pricingVersionValue = "dynamic";
          routeAddOptionsField.pricingConfig = true;
          const clonedParams = { ...cityCategoryPricing.config };
          Object.keys(clonedParams).forEach((pp) => {
            if (
              pp === "base_fare" ||
              pp === "distance_bucket_fare" ||
              pp === "time_bucket_fare"
            ) {
              clonedParams[pp] /= constants.currencyBy;
            }
            pricingParamterAddOptionsField[pp] = clonedParams[pp];
          });
        } else {
          rideCategoryAddOptionsField.pricingVersionValue = "fixed";
          routeAddOptionsField.pricingConfig = false;

          each((field) => {
            if (
              rideCategoryAddOptionsField.pricingVersionValue === "fixed" &&
              ![
                "corporate",
                "route",
                "ride_category",
                "captain",
                "supply_category",
                "contractor",
                "company",
              ].includes(field.name)
            ) {
              field.value = null;
              field.isDisabled = true;
            } else if (
              rideCategoryAddOptionsField.pricingVersionValue !== "fixed"
            ) {
              field.isDisabled = false;
            }
          })(this.addOptions.fields);
        }
        if (rideCategoryAddOptionsField.pricingVersionValue !== "fixed") {
          this.getCheckInConfiguration(
            constants.boardingCheckinLevels.category,
            selectedCategoryDetails.ride_type
          );
        }
      } else {
        pricingParamterAddOptionsField.active = false;
      }
    });
  }

  applyRidePrice() {
    const fields = this.addOptions.fields;
    if (
      isNil(this.route) ||
      isNil(this.activeContract) ||
      isNil(this.busType)
    ) {
      return;
    }
    const ridePrice = getContractRidePrice(
      this.busType,
      this.route,
      this.activeContract
    );
    each((field) => {
      if (field.name === "bus_type") {
        if (!ridePrice) {
          field.disclaimer =
            'This configuration does not exist in the corporate contract and will be treated as an "Out-of-contract Special Request". \n Please enter the ride price for the corporate invoice';
        } else if (field.disclaimer) delete field.disclaimer;
      }
      if (field.name === "ride_price") {
        if (isNil(this.busType)) {
          field.value = "";
          field.isDisabled = false;
        } else if (!ridePrice) {
          field.value = "";
          field.isDisabled = false;
        } else {
          field.value = ridePrice.pricing.ride_price / constants.currencyBy;
          field.isDisabled = true;
        }
      }
    })(fields);
  }

  getActiveContract(selected) {
    const corporate = selected;
    if (isNil(corporate)) return;
    each((field) => {
      if (
        field.name !== "corporate" &&
        field.name !== "route" &&
        field.name !== "ride_category" &&
        field.name !== "captain" &&
        field.name !== "bus" &&
        field.name !== "supply_category" &&
        field.name !== "contractor" &&
        field.name !== "company"
      ) {
        field.isDisabled = false;
        field.value = null;
      }
    })(this.addOptions.fields);
    this.getCheckInConfiguration(
      constants.boardingCheckinLevels.corporate,
      corporate._id
    );
    this.Contracts.getActive({
      corporateId: corporate._id,
    }).$promise.then((response) => {
      this.activeContract = response;
    });
  }

  onSelectSupplyCategory(category) {
    const fields = this.addOptions.fields;

    each((field) => {
      /** Make the 'captain' & `bus` fields required if no category is selected */
      if (field.name === "captain" || field.name === "bus") {
        if (category === "none") {
          field.isRequired = true;
        } else {
          field.isRequired = false;
        }
        /** Reset captain & bus values if exists */
        if (field.value) field.value = null;
        /** Reset captain params if exists */
        if (field.params) field.params = null;
      }
      /** Reset contractor value if exists */
      if (field.name === "contractor" && field.value) {
        field.value = null;
      }
      /** Reset company value if exists */
      if (field.name === "company" && field.value) {
        field.value = null;
      }
    })(fields);
  }

  onSelectSupply(params) {
    const fields = this.addOptions.fields;
    const supplyCategory = fields.find(
      (field) => field.name === "supply_category"
    ).value;

    each((field) => {
      if (field.name === "captain" && supplyCategory !== "none") {
        /** Reset captain value if exists */
        if (field.value) field.value = null;

        /** Pass contractor or company id as query params in the captain autocomplete search,
         * to filter only captains related */
        field.params = {
          [supplyCategory]: params._id,
        };
      }
      if (field.name === "bus") {
        /** Reset bus value if exists */
        if (field.value) field.value = null;
      }
    })(fields);
  }
}

export default RidesPageTransform;



// WEBPACK FOOTER //
// ./src/pages/rides/rides.transforms.js